import { generateAppearance } from "@unit/appearance-core";
import { organisationAppearanceService } from "@/services/organisationAppearanceService";
import {
  appearanceRuntime,
  clearOrganisationAppearance,
} from "@/services/appearanceRuntime";

const CACHE_PREFIX = "unit.orgAppearance.v1";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const cacheKey = (uid, orgId) => `${CACHE_PREFIX}.${uid}.${orgId}`;

const generateEffectiveAppearance = (effective) => {
  if (
    !effective ||
    effective.source === "unit" ||
    typeof effective.accentSeed !== "string"
  ) {
    return null;
  }
  return generateAppearance(effective.accentSeed);
};

const readCache = (uid, orgId) => {
  try {
    const value = JSON.parse(localStorage.getItem(cacheKey(uid, orgId)) || "null");
    if (
      !value ||
      value.paletteVersion !== 1 ||
      !["personal", "organisation", "unit"].includes(value.source) ||
      (value.accentSeed !== null && !/^#[0-9A-F]{6}$/.test(value.accentSeed)) ||
      !Number.isFinite(value.cachedAt) ||
      Date.now() - value.cachedAt > CACHE_TTL_MS
    ) {
      localStorage.removeItem(cacheKey(uid, orgId));
      return null;
    }
    return value;
  } catch (_error) {
    return null;
  }
};

const writeCache = (uid, response) => {
  try {
    localStorage.setItem(
      cacheKey(uid, response.orgId),
      JSON.stringify({
        paletteVersion: response.paletteVersion,
        source: response.effective.source,
        accentSeed: response.effective.accentSeed,
        personalRevision: response.layers.personal.revision,
        organisationRevision: response.layers.organisation.revision,
        cachedAt: Date.now(),
      }),
    );
  } catch (_error) {
    // A warm appearance cache is optional.
  }
};

const removeCache = (uid, orgId) => {
  try {
    localStorage.removeItem(cacheKey(uid, orgId));
  } catch (_error) {
    // Nothing else is required when storage is unavailable.
  }
};

const removeUserCache = (uid) => {
  if (!uid) return;
  try {
    const prefix = `${CACHE_PREFIX}.${uid}.`;
    Object.keys(localStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => localStorage.removeItem(key));
  } catch (_error) {
    // Best-effort privacy cleanup.
  }
};

const canUseMembership = (rootState, uid, orgId) =>
  typeof uid === "string" &&
  rootState.userUnitsOwnerUID === uid &&
  rootState.userUnitsData?.[orgId]?.isActiveMember !== false &&
  rootState.userUnitsData?.[orgId]?.membershipStatus !== "inactive" &&
  Boolean(rootState.userUnitsData?.[orgId]);

const effectiveFromCache = (orgId, cached) => ({
  schemaVersion: 1,
  paletteVersion: 1,
  orgId,
  effective: {
    source: cached.source,
    accentSeed: cached.accentSeed,
  },
  layers: {
    personal: {
      state: cached.source === "personal" ? "active" : "reset",
      revision: cached.personalRevision,
      accentSeed: cached.source === "personal" ? cached.accentSeed : null,
    },
    organisation: {
      state: cached.source === "organisation" ? "active" : "reset",
      revision: cached.organisationRevision,
      accentSeed: cached.source === "organisation" ? cached.accentSeed : null,
    },
  },
  capabilities: { canManageOrganisationAppearance: false },
  fromCache: true,
});

export default {
  namespaced: true,
  state: () => ({
    activeOwnerUid: null,
    activeOrgId: null,
    activeSource: "unit",
    activeAccentSeed: null,
    records: {},
    loading: {},
    errors: {},
    requestGeneration: 0,
    requestGenerations: {},
  }),
  getters: {
    recordForOrg: (state) => (orgId) => state.records[orgId] || null,
    isLoadingOrg: (state) => (orgId) => state.loading[orgId] === true,
    errorForOrg: (state) => (orgId) => state.errors[orgId] || null,
    activeAppearance: (state) => ({
      orgId: state.activeOrgId,
      source: state.activeSource,
      accentSeed: state.activeAccentSeed,
    }),
  },
  mutations: {
    BEGIN_REQUEST(state, { orgId }) {
      state.requestGeneration += 1;
      state.requestGenerations = {
        ...state.requestGenerations,
        [orgId]: state.requestGeneration,
      };
      state.loading = { ...state.loading, [orgId]: true };
      state.errors = { ...state.errors, [orgId]: null };
    },
    SET_RECORD(state, { orgId, record }) {
      state.records = { ...state.records, [orgId]: record };
      state.loading = { ...state.loading, [orgId]: false };
      state.errors = { ...state.errors, [orgId]: null };
    },
    SET_ERROR(state, { orgId, error }) {
      state.loading = { ...state.loading, [orgId]: false };
      state.errors = { ...state.errors, [orgId]: error };
    },
    SET_ACTIVE(state, { uid, orgId, source, accentSeed }) {
      state.activeOwnerUid = uid;
      state.activeOrgId = orgId;
      state.activeSource = source;
      state.activeAccentSeed = accentSeed;
    },
    REMOVE_RECORD(state, { orgId }) {
      const { [orgId]: _record, ...records } = state.records;
      const { [orgId]: _loading, ...loading } = state.loading;
      const { [orgId]: _error, ...errors } = state.errors;
      const { [orgId]: _generation, ...requestGenerations } =
        state.requestGenerations;
      state.records = records;
      state.loading = loading;
      state.errors = errors;
      state.requestGenerations = requestGenerations;
    },
    CLEAR(state) {
      state.requestGeneration += 1;
      state.activeOwnerUid = null;
      state.activeOrgId = null;
      state.activeSource = "unit";
      state.activeAccentSeed = null;
      state.records = {};
      state.loading = {};
      state.errors = {};
      state.requestGenerations = {};
    },
  },
  actions: {
    async fetchForEditor({ commit, state, rootState }, { uid, orgId, force = false }) {
      if (!canUseMembership(rootState, uid, orgId)) {
        const error = new Error("This organisation is not an active membership.");
        error.code = "permission-denied";
        throw error;
      }
      if (!force && state.records[orgId] && !state.records[orgId].fromCache) {
        return state.records[orgId];
      }

      commit("BEGIN_REQUEST", { orgId });
      const generation = state.requestGenerations[orgId];
      try {
        const response = await organisationAppearanceService.getOwnOrganisationAppearance(orgId);
        if (
          generation !== state.requestGenerations[orgId] ||
          !canUseMembership(rootState, uid, orgId)
        ) {
          return null;
        }
        commit("SET_RECORD", { orgId, record: response });
        writeCache(uid, response);
        return response;
      } catch (error) {
        if (generation === state.requestGenerations[orgId]) {
          commit("SET_ERROR", { orgId, error });
        }
        throw error;
      }
    },

    async loadForOrganisation(
      { commit, dispatch, rootState, state },
      { uid, orgId, force = false },
    ) {
      if (!orgId || !canUseMembership(rootState, uid, orgId)) {
        clearOrganisationAppearance();
        commit("SET_ACTIVE", {
          uid: uid || null,
          orgId: null,
          source: "unit",
          accentSeed: null,
        });
        return null;
      }

      let warmAppearanceApplied = false;
      if (!force) {
        const cached = readCache(uid, orgId);
        if (cached) {
          const record = effectiveFromCache(orgId, cached);
          commit("SET_RECORD", { orgId, record });
          appearanceRuntime.applyGenerated(generateEffectiveAppearance(record.effective));
          commit("SET_ACTIVE", {
            uid,
            orgId,
            source: cached.source,
            accentSeed: cached.accentSeed,
          });
          warmAppearanceApplied = true;
        } else if (
          state.activeOwnerUid === uid &&
          state.activeOrgId === orgId
        ) {
          // A Wallet summary can provide the effective seed before the
          // authoritative appearance read finishes. Keep that immediate
          // feedback in place instead of briefly flashing back to Unit.
          warmAppearanceApplied = true;
        }
      }

      if (!warmAppearanceApplied) {
        clearOrganisationAppearance();
        commit("SET_ACTIVE", {
          uid,
          orgId,
          source: "unit",
          accentSeed: null,
        });
      }

      try {
        const record = await dispatch("fetchForEditor", { uid, orgId, force: true });
        if (
          !record ||
          rootState.selectedOrgUID !== orgId ||
          rootState.selectedOrgOwnerUID !== uid
        ) {
          return record;
        }
        appearanceRuntime.applyGenerated(
          generateEffectiveAppearance(record.effective),
        );
        commit("SET_ACTIVE", {
          uid,
          orgId,
          source: record.effective.source,
          accentSeed: record.effective.accentSeed,
        });
        return record;
      } catch (error) {
        if (error?.code === "permission-denied") {
          const wasActive =
            state.activeOwnerUid === uid && state.activeOrgId === orgId;
          removeCache(uid, orgId);
          commit("REMOVE_RECORD", { orgId });
          if (wasActive) {
            clearOrganisationAppearance();
            commit("SET_ACTIVE", {
              uid,
              orgId: null,
              source: "unit",
              accentSeed: null,
            });
          }
          try {
            await dispatch(
              "ensureSelectedOrg",
              { userUID: uid, force: true },
              { root: true },
            );
          } catch (_reconciliationError) {
            // The authoritative denial still wins if membership refresh fails.
          }
        }
        if (error?.code === "feature-disabled") {
          clearOrganisationAppearance();
          commit("SET_ACTIVE", { uid, orgId, source: "unit", accentSeed: null });
          return null;
        }
        throw error;
      }
    },

    async saveAppearance(
      { commit, state, rootState },
      { uid, orgId, scope, action, expectedRevision, accentSeed, operationId },
    ) {
      if (!canUseMembership(rootState, uid, orgId)) {
        const error = new Error("This organisation is not an active membership.");
        error.code = "permission-denied";
        throw error;
      }
      commit("BEGIN_REQUEST", { orgId });
      const generation = state.requestGenerations[orgId];
      try {
        const result =
          await organisationAppearanceService.setOwnOrganisationAppearance({
            operationId,
            orgId,
            scope,
            action,
            expectedRevision,
            accentSeed,
          });
        removeCache(uid, orgId);
        if (
          !canUseMembership(rootState, uid, orgId) ||
          generation !== state.requestGenerations[orgId]
        ) {
          return { result, record: result, stale: true };
        }
        commit("SET_RECORD", { orgId, record: result });
        writeCache(uid, result);
        if (
          rootState.selectedOrgUID === orgId &&
          rootState.selectedOrgOwnerUID === uid
        ) {
          appearanceRuntime.applyGenerated(
            generateEffectiveAppearance(result.effective),
          );
          commit("SET_ACTIVE", {
            uid,
            orgId,
            source: result.effective.source,
            accentSeed: result.effective.accentSeed,
          });
        }
        return { result, record: result, stale: false };
      } catch (error) {
        if (generation === state.requestGenerations[orgId]) {
          commit("SET_ERROR", { orgId, error });
        }
        throw error;
      }
    },

    async applyWalletSummarySeed(
      { commit, rootState },
      { uid, orgId, accentSeed, source = "organisation" },
    ) {
      if (
        !canUseMembership(rootState, uid, orgId) ||
        rootState.selectedOrgUID !== orgId
      ) {
        return false;
      }
      appearanceRuntime.applyGenerated(
        accentSeed ? generateAppearance(accentSeed) : null,
      );
      commit("SET_ACTIVE", { uid, orgId, source, accentSeed: accentSeed || null });
      return true;
    },

    clearOrganisation({ state, commit }, { uid = state.activeOwnerUid, orgId } = {}) {
      if (uid && orgId) removeCache(uid, orgId);
      clearOrganisationAppearance();
      commit("SET_ACTIVE", { uid: uid || null, orgId: null, source: "unit", accentSeed: null });
    },

    forgetOrganisation({ state, commit }, { uid = state.activeOwnerUid, orgId } = {}) {
      if (!orgId) return;
      if (uid) removeCache(uid, orgId);
      commit("REMOVE_RECORD", { orgId });
      if (state.activeOrgId === orgId) {
        clearOrganisationAppearance();
        commit("SET_ACTIVE", {
          uid: uid || null,
          orgId: null,
          source: "unit",
          accentSeed: null,
        });
      }
    },

    clearForAuthChange({ state, commit }, payload = {}) {
      const previousUid = payload?.previousUid ?? state.activeOwnerUid;
      removeUserCache(previousUid);
      clearOrganisationAppearance();
      commit("CLEAR");
    },
  },
};

export { CACHE_TTL_MS, cacheKey };
